import React, {Fragment, useEffect, useState} from 'react';
import PropTypes from "prop-types";

import ModalContentWrapper from '../ModalContentWrapper';
import '../TwoColumnModal.scss';
import './ModalReportDateRange.scss';

import SpinnerDiv from "root/Forms/UI/SpinnerDiv";

import { useForm } from "react-hook-form";

import format from "date-fns/format";

import DatePickerControl from "root/ReactHookForm/ControlWrappers/DatePickerControl";

import DropdownAlternateProviders from "root/ReactHookForm/ui/DropdownAlternateProviders";
import {getDataFromResponse} from "root/DataFunctions/BaseDataFns";
import {hookFormDate, hookFormDropdownValue} from "root/Base/HookFormComponents/hookFormValidation";
import {createPlacementForChild, endPlacementDetailsForChild, getChildAltProviderDetails, updatePlacementDetailsForChild} from "root/DataFunctions/AltProviderDataFns";
import {SmartErrorDiv} from "root/ReactHookForm/FormComponents";
import DropdownAlternateProvisionReason from "root/ReactHookForm/ui/DropdownAlternateProvisionReason";
import BootstrapRow from "ui/BootstrapRow";


export const ModalAlternateProviderModes = {
    CREATE: 'select',
    EDIT: 'edit',
    DELETE: 'delete',
    VIEW: 'view',
}

const SaveButton = ({state, onClick, children}) =>
{
    const clickHandler = state === '' ? onClick : null;

    return (
        <button onClick={clickHandler} style={{marginLeft: '10px'}} disabled={state}
                className='btn btn-icon btn-primary btn-icon-right btn-small'>{children}<i className="et-save"/>
        </button>
    )
}

const ModalAlternateProviders = ({ child_id, cap_id, onSaveCompleted, onClose, mode, width, transferData }) =>
{
    const [buttonState, setButtonState] = useState('');

    const isLoading = mode === ModalAlternateProviderModes.EDIT || mode === ModalAlternateProviderModes.VIEW || mode === ModalAlternateProviderModes.DELETE ;

    const isFetchRequired = mode === ModalAlternateProviderModes.EDIT || mode === ModalAlternateProviderModes.VIEW || mode === ModalAlternateProviderModes.DELETE ;
    const readOnlyMode = mode === ModalAlternateProviderModes.VIEW || mode === ModalAlternateProviderModes.DELETE ;

    const [data, setData] = useState({isLoading: isLoading, data: []});

    const initDate = transferData !== null ? hookFormDate(transferData.date_from, 'dd/MM/yyyy') : null ;
    const initAgreementDate = transferData !== null ? hookFormDate(transferData.agreement_date, 'dd/MM/yyyy') : null ;
    const initNumberOfHours = transferData !== null ? transferData.number_of_hours : null ;
    const initDetails = transferData !== null ? transferData.details + '\n' + transferData.days : null ;
    const initAPReason = transferData !== null ? transferData.ap_reason_id : null ;

    const {register, control, reset, resetField,  handleSubmit, setFocus, watch, setValue, formState: {errors: formErrors}} = useForm(
        {
            defaultValues: {
            }
        });

    const getDefaultData = () =>
    {
        return {
            provider: {
                school_id: null,
                date_from: initDate,
                date_to: null,
                agreement_date: initAgreementDate,
                ap_reason_id: hookFormDropdownValue ( initAPReason ),
                number_of_hours: initNumberOfHours,
                details : initDetails,
            }
        }
    }

    useEffect(() =>
    {
        if ( isFetchRequired )
        {
            getChildAltProviderDetails(child_id, cap_id, [], (response) =>
            {
                let capData = getDataFromResponse(response)[0] ;

                setData({ isLoading: false, data: capData });
            });
        }
    }, []);

    useEffect(() =>
    {
        // This triggers after the data is loaded and isLoading toggles to false
        // At this point we know the data is ready to be rendered

        if ( data.isLoading === false )
        {
            const rec = data.data ;

            let defaults ;

            if ( mode === ModalAlternateProviderModes.CREATE )
            {
                defaults = getDefaultData() ;
            }
            else
            {
                defaults =
                {
                    provider: {
                        school_id: hookFormDropdownValue ( rec.school_id ),
                        ap_reason_id: hookFormDropdownValue ( rec.ap_reason_id ),
                        date_from: hookFormDate(rec.date_from, 'dd/MM/yyyy'),
                        date_to: hookFormDate(rec.date_to, 'dd/MM/yyyy'),
                        agreement_date: hookFormDate(rec.agreement_date, 'dd/MM/yyyy'),
                        number_of_hours: rec.number_of_hours,
                        details: rec.details
                    }
                }
            }

            reset(defaults);
        }
    }, [data.isLoading]);

    const onSaveComplete = ( result ) =>
    {
        onSaveCompleted( result );
    }

    const onClickCancel = () =>
    {
        onClose();
    }

    const doSave = (data) =>
    {
        switch ( mode )
        {
            case ModalAlternateProviderModes.CREATE:
                createPlacementForChild ( child_id, data, (response) =>
                {
                    onSaveCompleted( 'saved' );
                });
            break;
            case ModalAlternateProviderModes.EDIT:
                updatePlacementDetailsForChild ( child_id, cap_id, data, (response) =>
                {
                    onSaveCompleted( 'saved' );
                });
            break;
            case ModalAlternateProviderModes.DELETE:
                endPlacementDetailsForChild ( child_id, cap_id, data, (response) =>
                {
                    onSaveCompleted( 'saved' );
                });
                break ;
            default:
                throw new Error ("Invalid mode passed to ModalDelegatePep") ;
        }
    }

    const disableButtons = () =>
    {
        setButtonState('disabled');
    }

    const getSaveButtonText = () =>
    {
        const saveButtonText = {
            [ModalAlternateProviderModes.CREATE]: 'Save',
            [ModalAlternateProviderModes.EDIT]: 'Save Changes',
            [ModalAlternateProviderModes.DELETE]: 'Delete Record',
        };

        return saveButtonText[mode];
    }

    const onSubmit = (data) =>
    {
        disableButtons();

        const start = format(data.provider.date_from, 'dd/MM/yyyy') ;
        const agreement_date = format(data.provider.agreement_date, 'dd/MM/yyyy') ;
        const date_to = data.provider.date_to === null ? null : format(data.provider.date_to, 'dd/MM/yyyy') ;

        const school_id = data.provider.school_id === null ? null : data.provider.school_id.value ;
        const ap_reason_id = data.provider.ap_reason_id === null ? null : data.provider.ap_reason_id.value ;

        const formData = {
            provider:
                {
                    date_from: start,
                    agreement_date: agreement_date,
                    date_to: date_to,
                    school_id: school_id,
                    ap_reason_id: ap_reason_id,
                    number_of_hours: data.provider.number_of_hours,
                    details: data.provider.details,
                }
        }

        doSave(formData);
    }

    const onClickSaveForm = (status) =>
    {
        handleSubmit(onSubmit)(status);
    }

    const validateProvider = (val) =>
    {
        if (val === null)
        {
            return "Please Select a provider.";
        }
    }

    const validateStartDate = (val) =>
    {
        if (val === null)
        {
            return "Please Select a Date.";
        }
    }

    const validateAgreementDate = (val) =>
    {
        if (val === null)
        {
            return "Please Select a Date.";
        }
    }

    const validateEndDate = (val) =>
    {
        if (val === null)
        {
            return "Please Select a Date.";
        }
    }
    const validateAPReason= (val) =>
    {
        if (val === null)
        {
            return "Please Select a reason.";
        }
    }

    const formContent = () =>
    {
        if (data.isLoading === true)
        {
            return (
                <div style={{width: width}}>
                    <SpinnerDiv/>
                </div>
            )
        }
        else if (mode === ModalAlternateProviderModes.CREATE || mode === ModalAlternateProviderModes.EDIT || mode === ModalAlternateProviderModes.VIEW || mode === ModalAlternateProviderModes.DELETE )
        {

            return (
                <Fragment>

                    <div className='' style={{marginBottom: '20px', marginTop: '20px'}}>
                        <div style={{marginBottom: '15px'}}>Modify the details below as required and click the <span>{getSaveButtonText(mode)}</span> button below to save
                        </div>
                        <form className='' onSubmit={handleSubmit(onSubmit)}>

                            <BootstrapRow>
                                <div className='col-md-12'>
                                    <label>Alternative Provider</label>
                                    <DropdownAlternateProviders name='provider.school_id' control={control}
                                                          rules={{validate: validateProvider}}
                                                          placeHolder='Please Select a provider' readOnly={readOnlyMode}/>
                                    <SmartErrorDiv sectionName='provider'  name={'school_id'} formErrors={formErrors} />
                                </div>
                            </BootstrapRow>

                            <BootstrapRow>
                                <div className='col-md-12'>
                                    <label>Details</label>
                                    <textarea readOnly={readOnlyMode}
                                           className='form-control' {...register("provider.details", {
                                        required: true,
                                        maxLength: 2000
                                    })} />
                                    <SmartErrorDiv sectionName='provider'  name={'details'} formErrors={formErrors} />
                                </div>
                            </BootstrapRow>

                            <BootstrapRow>
                                <div className='col-md-6'>
                                    <label>Start Date</label>
                                    <DatePickerControl name='provider.date_from' control={control}
                                                       validate={validateStartDate}
                                                       placeHolder='Please Select Start Date' readOnly={readOnlyMode}
                                                       showTimeInput={false}/>
                                    <SmartErrorDiv sectionName='provider'  name={'date_from'} formErrors={formErrors} />
                                </div>

                                <div className='col-md-6'>
                                    <label>Agreement Date</label>
                                    <DatePickerControl name='provider.agreement_date' control={control}
                                                       validate={validateAgreementDate}
                                                       placeHolder='Please Select Agreement Date' readOnly={readOnlyMode}
                                                       showTimeInput={false}/>
                                    <SmartErrorDiv sectionName='provider' name={'agreement_date'} formErrors={formErrors} />
                                </div>
                            </BootstrapRow>

                            { mode === ModalAlternateProviderModes.DELETE && <BootstrapRow>
                                <div className='col-md-6'>
                                    <label>End Date</label>
                                    <DatePickerControl name='provider.date_to' control={control}
                                                       validate={validateEndDate}
                                                       placeHolder='Please Select End Date' readOnly={false}
                                                       showTimeInput={false}/>
                                    <SmartErrorDiv sectionName='provider'  name={'date_to'} formErrors={formErrors} />
                                </div>

                                <div className='col-md-6'>
                                </div>
                            </BootstrapRow> }

                            <BootstrapRow>
                                <div className='col-md-12'>
                                    <label>Number of Hours</label>
                                    <input readOnly={readOnlyMode}
                                           className='form-control' {...register("provider.number_of_hours", {
                                        required: true,
                                        maxLength: 3
                                    })} />
                                    <SmartErrorDiv sectionName='provider' name={'number_of_hours'} maxLength={3} formErrors={formErrors} />
                                </div>
                            </BootstrapRow>

                            <BootstrapRow>
                                <div className='col-md-12'>
                                    <label>Select New Education Status</label>
                                    <DropdownAlternateProvisionReason name='provider.ap_reason_id' control={control} rules={{validate: validateAPReason}} placeHolder='Please Select a status' readOnly={readOnlyMode}/>
                                    <SmartErrorDiv sectionName='provider' name={'ap_reason_id'} formErrors={formErrors} />
                                </div>
                            </BootstrapRow>

                        </form>
                    </div>

                    <div className="modal-button-row">
                        <button onClick={onClickCancel} className="btn btn-secondary">Cancel<i
                            className="et-arrow-bold-left"/></button>
                        <SaveButton state={buttonState} onClick={onClickSaveForm}>
                            {getSaveButtonText(mode)}
                        </SaveButton>
                    </div>

                </Fragment>
            )
        }
    }

    const title = () =>
    {
        const headerStyle = {marginBottom: '10px'};

        switch ( mode )
        {
            case ModalAlternateProviderModes.DELETE:
                return <h3 style={headerStyle}>Remove Alternate Provider From Pupil</h3>

            case ModalAlternateProviderModes.EDIT:
                return <h3 style={headerStyle}>Change Alternate Provider Details</h3>
        }
    }

    return (
        <ModalContentWrapper className="modal-delegate-pep" width={'600px'} columns={1}>
            {title()}
            {formContent()}
        </ModalContentWrapper>
    )
}

export default ModalAlternateProviders;

ModalAlternateProviders.propTypes = {
    pep_id: PropTypes.string,
    section: PropTypes.string,
    mode: PropTypes.oneOf([ModalAlternateProviderModes.CREATE, ModalAlternateProviderModes.EDIT, ModalAlternateProviderModes.DELETE]),
};